import { Can, useAbility } from "@/casl.config";
import NoPermissionBanner from "@components/ErrorPages/NoPermissionBanner";
import { useGetProductDraftQuery } from "@redux/features/productDrafts/productDraftsApi";
import BaseBadge from "@reusables/BaseBadge";
import BaseRecordRenderer from "@reusables/BaseRecordRenderer";
import { Box, Forklift } from "lucide-react";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router-dom";
import CreationEditingLayout from "../components/CreationEditingLayout";

export default function ProductDraftEditingPage() {
    const history = useHistory();
    const ability = useAbility();
    const { t } = useTranslation("", { keyPrefix: "productDrafts" });
    const { id } = useParams<{ id: string }>();
    const draftId = Number(id);

    if (!Number.isInteger(draftId) || draftId <= 0) {
        throw new Error(`Invalid Product Draft id: ${id}`);
    }

    const { data: productDraft, isLoading } = useGetProductDraftQuery(draftId, {
        skip: ability.cannot("view", "product"),
    });

    useEffect(() => {
        if (productDraft?.status === "exported") {
            history.replace(
                `/dashboard/products/${productDraft.exported_product_id as number}/details`
            );
        }
    }, [history, productDraft?.exported_product_id, productDraft?.status]);

    if (productDraft?.status === "exported" && productDraft.exported_product_id == null) {
        throw new Error(
            `Materialized Product Draft [${productDraft.id}] is missing exported_product_id`
        );
    }

    if (productDraft?.status === "exported") return null;

    const statusClass = productDraft?.status === "failed" ? "bg-badge-red" : "bg-badge-yellow";

    return (
        <>
            <Can not I="edit" a="product">
                <div className="levitation-extended">
                    <NoPermissionBanner />
                </div>
            </Can>

            <Can I="edit" a="product">
                <BaseRecordRenderer
                    loading={isLoading}
                    record={productDraft}
                    notFoundText={t("viewEditProduct.notFound")}
                >
                    {productDraft && (
                        <div className="space-y-[40px]">
                            <div className="flex flex-wrap items-center justify-between gap-[16px]">
                                <div className="flex items-center gap-[8px]">
                                    <h1 className="text-2xl font-bold text-accent">
                                        {productDraft.product.name}
                                    </h1>
                                    {productDraft.product.is_service ? (
                                        <BaseBadge className="bg-light-green">
                                            <Forklift size={12} className="mr-1" />
                                            <span>{t("editor.type.service")}</span>
                                        </BaseBadge>
                                    ) : (
                                        <BaseBadge className="bg-purple-400 text-white">
                                            <Box size={12} className="mr-1 -mt-0.5" />
                                            <span>{t("editor.type.product")}</span>
                                        </BaseBadge>
                                    )}
                                </div>
                                <BaseBadge className={statusClass}>
                                    {productDraft.status === "failed"
                                        ? t("allProducts.table.status.failed")
                                        : t("allProducts.table.status.draft")}
                                </BaseBadge>
                            </div>

                            <CreationEditingLayout productDraft={productDraft} />
                        </div>
                    )}
                </BaseRecordRenderer>
            </Can>
        </>
    );
}
